import { ROUTES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { ReadonlyURLSearchParams, useRouter, useSearchParams } from "next/navigation"
import * as R from "ramda"
import { useEffect } from "react"

import { getQuote } from "../http"
import { Utms } from "../types/global-store"
import { SyncBackBody } from "../types/lead-api-types"
import { computeRedirectRoute } from "../utils/compute-redirect-route"
import { generatePetId, getOrCreateUserId } from "../utils/helpers"
import { addQuoteIdToTrackJS } from "../utils/logs"
import { saveQuoteIdCookie } from "../utils/quote-cookie"
import { storeQuoteId, storeUserId } from "../utils/storage"
import { type Route } from "next"

type SearchParams = { [key: string]: string | string[] | undefined }

const UTM_PARAM_KEYS: (keyof Utms)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_comparateur",
  "gclid",
  "compclid",
  "at_gd",
  "advertiser_id",
  "channel_id",
  "market",
  "click_id",
  "click_ref",
  "s_id",
  "affiliate_gclid",
  "transaction_id",
]

const toSingleValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value

const extractUtmParams = (searchParams: ReadonlyURLSearchParams): Utms => {
  const utms = {} as Utms

  UTM_PARAM_KEYS.forEach((key) => {
    const value = toSingleValue(searchParams.get(key as string) ?? undefined)
    if (value) {
      utms[key] = value
    }
  })

  if (!utms.affiliate_gclid) {
    const gclidParam = searchParams.get("affiliateGclid") ?? undefined
    const fallbackAffiliateGclid = toSingleValue(gclidParam)
    if (fallbackAffiliateGclid) {
      utms.affiliate_gclid = fallbackAffiliateGclid
    }
  }

  return utms
}

const mergeQuoteWithMeta = (
  currentQuote: Partial<SyncBackBody> | undefined,
  quotePatch: Partial<SyncBackBody>,
  utmParams: Utms
): Partial<SyncBackBody> => {
  const existingQuote = currentQuote || {}
  const existingMeta = existingQuote.meta || {}
  const patchMeta = quotePatch.meta || {}

  return {
    ...existingQuote,
    ...quotePatch,
    meta: {
      ...existingMeta,
      ...patchMeta,
      utm: {
        ...utmParams,
        ...(patchMeta.utm || {}),
        ...(existingMeta.utm || {}),
      },
    },
  }
}

export const formatQuoteResponse = (
  quoteResponse: Awaited<ReturnType<typeof getQuote>>,
  utmParams?: Utms,
  searchParams?: ReadonlyURLSearchParams,
  existingUserId?: string
): Partial<SyncBackBody> => ({
  // Sort by pet_idx — the backend does not guarantee insertion order, and
  // downstream UI (tribe, tribe-quote cards) renders pets in array order.
  // @ts-expect-error pet is good
  pets: [...quoteResponse.data]
    .sort((a, b) => (a.pet_idx ?? 0) - (b.pet_idx ?? 0))
    .map((pet) => ({
      name: pet.pet_name,
      created_at: pet.start_date,
      id: generatePetId(),
      idx: pet.pet_idx,
      type: pet.pet_type as "cat" | "dog",
      sexe: pet.pet_sexe as "MALE" | "FEMALE",
      birthday: pet.pet_birthday,
      breed: { race: pet.pet_race },
      price: pet.price as number,
      after_discount: pet.after_discount,
      health: {
        status: "healthy",
      },
      // Restore the RC add-on carried by the pet's liability bundle leg so it
      // survives a retrieve / redirect (cross-device, no sessionStorage). Kept as
      // has_liability (true | undefined today; a future explicit false is honored
      // by the "backend wins" fallback checks).
      liability: pet.has_liability,
      rc_price: pet.liability_price,
      has_previous_insurance: pet.liability_has_previous_insurance,
      number_of_previous_claims: pet.liability_number_of_previous_claims as
        | 0
        | 1
        | 2
        | 3
        | undefined,
      pricing:
        pet.options || pet.coverage
          ? {
              accepted_formula: "formula_dalma_de",
              options: pet.options
                ? {
                    ...pet.options,
                  }
                : null,
              coverage: pet.coverage
                ? {
                    ...pet.coverage,
                  }
                : null,
            }
          : null,
    })),
  owner: {
    email:
      quoteResponse.pet_owner.owner_email && quoteResponse.pet_owner.owner_email !== "_"
        ? quoteResponse.pet_owner.owner_email
        : null,
    phone: quoteResponse.pet_owner.owner_phone || "+49000000000",
    firstname: quoteResponse.pet_owner.owner_firstname || null,
    lastname: quoteResponse.pet_owner.owner_lastname || null,
    birthcity: quoteResponse.pet_owner.owner_birthcity || null,
    birthday: quoteResponse.pet_owner.owner_birthday || null,
    address: quoteResponse.pet_owner.address
      ? {
          ...quoteResponse.pet_owner.address,
        }
      : null,
  },
  meta: {
    quote_id: quoteResponse.data[0]?.quote_id,
    user_id: quoteResponse.data[0]?.user_id || existingUserId || getOrCreateUserId(),
    pricing_version: quoteResponse.data[0]?.pricing_version,
    start_date: quoteResponse.data[0]?.start_date,
    subscription_mode: quoteResponse.data[0]?.subscription_mode,
    raf_code: quoteResponse.data[0]?.raf_code,
    promo_code:
      quoteResponse.data[0]?.promo_code ||
      toSingleValue(searchParams?.get("promo_code") ?? undefined),
    ab_test: quoteResponse.data[0]?.ab_test,
    utm: {
      ...(utmParams || {}),
      ...(quoteResponse.data?.[0]?.utm_params || {}),
    },
  },
  marketing: {
    ...(quoteResponse.data?.[0]?.marketing || {}),
  },
})

export function useQuoteFetching(searchParams: SearchParams, isFromLandingPage = false): null {
  const [state, send] = useActor(newSubscriptionFlowService)
  const newSearchParams = useSearchParams()
  const router = useRouter()

  useEffect(() => {
    const utmParams = extractUtmParams(newSearchParams)
    const petName = toSingleValue(newSearchParams.get("petName") ?? undefined)
    const quoteId = toSingleValue(newSearchParams.get("quote_id") ?? undefined)
    const promoCode = toSingleValue(newSearchParams.get("promo_code") ?? undefined)
    const updateQuoteContext = (quotePatch: Partial<SyncBackBody>) => {
      send({
        type: "UPDATE_QUOTE",
        quote: mergeQuoteWithMeta(state.context.quote, quotePatch, utmParams),
      })
    }

    if (!quoteId) {
      if (petName) {
        const petId = generatePetId()
        updateQuoteContext({
          pets: [
            {
              name: petName,
              idx: 0,
              id: petId,
            },
          ],
          meta: R.reject(R.isNil, {
            promo_code: promoCode || undefined,
          }),
        })
        send({
          type: "SET_CURRENT_PET_IDX",
          petIndex: 0,
          petId,
        })
        router.push(ROUTES.petInfo.path)
        return
      }

      updateQuoteContext({
        meta: R.reject(R.isNil, {
          promo_code: promoCode || undefined,
        }),
      })
      router.push(ROUTES.petName.path)
      return
    }

    const fetchQuote = async () => {
      try {
        const quote = await getQuote(quoteId)
        const formattedQuote = formatQuoteResponse(
          quote,
          utmParams,
          newSearchParams,
          state.context.quote?.meta?.user_id
        )
        storeQuoteId(quoteId)
        storeUserId(formattedQuote.meta?.user_id)
        saveQuoteIdCookie(quoteId)
        addQuoteIdToTrackJS(quoteId)
        if (isFromLandingPage) {
          send({
            type: "SET_FROM_LANDING_PAGE",
          })
        }
        send({
          type: "UPDATE_QUOTE",
          quote: formattedQuote,
        })
        // Re-link the bundle so the RC add-on survives the redirect and the
        // bundled checkout works (liability lives in a separate quote).
        if (quote.bundle_id) {
          send({
            type: "SET_BUNDLE_DATA",
            bundle_id: quote.bundle_id,
            liability_quote_id: quote.liability_quote_id ?? null,
          })
        }

        const baseRedirectRoute = computeRedirectRoute(quote)
        const redirectRoute =
          isFromLandingPage && baseRedirectRoute === ROUTES.tribe.path ? ROUTES.prepackages.path : baseRedirectRoute
        const petWithoutFormula = formattedQuote.pets?.find((p) => !p.pricing)
        const targetPet =
          redirectRoute === ROUTES.prepackages.path && petWithoutFormula
            ? petWithoutFormula
            : formattedQuote.pets?.[0]
        if (targetPet) {
          send({
            type: "SET_CURRENT_PET_IDX",
            petId: targetPet.id,
            petIndex: targetPet.idx,
          })
        }
        router.push(redirectRoute as Route)
      } catch {
        updateQuoteContext({})
        router.push(ROUTES.petName.path)
      }
    }

    void fetchQuote()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch keyed on pathname; adding state/router/send would re-fire redirects
  }, [])

  return null
}
